/**
 * Temu「货品名称长度限制规则」（更新时间 2026-05-25）。
 * 按一级类目 cat1Id 限制 productName 加权长度；未命中清单默认 500。
 *
 * 计权：英文字母、数字、英文标点（含空格等 ASCII 可打印）算 1；其余字符（如中文）算 2。
 */

/** @type {Readonly<Record<string, number>>} */
export const TEMU_PRODUCT_NAME_LIMIT_BY_CAT1 = Object.freeze({
  1: 100, // CD和黑胶唱片
  653: 250, // 办公用品
  1464: 250, // 宠物用品
  2096: 250, // 大家电
  2542: 250, // 电子
  3680: 350,
  4673: 250, // 工业和科学
  9711: 250, // 家具厨房用品
  13512: 250, // 家居装修
  15945: 250, // 健康和家具用品
  17719: 250, // 乐器
  18768: 250, // 美容和个人护理
  19858: 250, // 汽车用品
  23177: 250, // 视频游戏
  24252: 250, // 手机和配件
  24389: 250, // 庭院草坪和园艺
  26205: 350,
  26207: 250, // 母婴用品
  27011: 250, // 服装鞋靴和珠宝饰品
  31148: 250, // 运动与户外用品
});

export const TEMU_PRODUCT_NAME_DEFAULT_LIMIT = 500;

/** ASCII 可打印（含空格与英文标点）→ 计 1；其余 → 计 2 */
const ASCII_PRINTABLE = /^[\x20-\x7E]$/;

/**
 * @param {string|null|undefined} text
 * @returns {number}
 */
export function countTemuProductNameWeight(text) {
  const s = String(text ?? '');
  let weight = 0;
  for (const ch of s) {
    weight += ASCII_PRINTABLE.test(ch) ? 1 : 2;
  }
  return weight;
}

/**
 * @param {string|number|null|undefined} cat1Id
 * @returns {number}
 */
export function resolveTemuProductNameLimit(cat1Id) {
  const id = String(cat1Id ?? '').trim();
  if (id && Object.prototype.hasOwnProperty.call(TEMU_PRODUCT_NAME_LIMIT_BY_CAT1, id)) {
    return TEMU_PRODUCT_NAME_LIMIT_BY_CAT1[id];
  }
  return TEMU_PRODUCT_NAME_DEFAULT_LIMIT;
}

/**
 * 从发品表单解析一级类目 ID（cat1Id）。
 * @param {object} [form]
 * @returns {string}
 */
export function resolveTemuCat1Id(form = {}) {
  const path = Array.isArray(form.catPathIds) && form.catPathIds.length
    ? form.catPathIds
    : (Array.isArray(form.categoryPathIds) && form.categoryPathIds.length
      ? form.categoryPathIds
      : null);
  if (path?.length) {
    const id = String(path[0] ?? '').trim();
    if (id) return id;
  }
  const cat1 = form.cat1Id ?? form.cat1_id;
  if (cat1 != null && String(cat1).trim()) return String(cat1).trim();
  return '';
}

/**
 * @param {object} [form]
 * @returns {number}
 */
export function resolveTemuProductNameLimitForForm(form = {}) {
  return resolveTemuProductNameLimit(resolveTemuCat1Id(form));
}

/**
 * 截断到不超过 limit 加权长度（尽量保留前缀）。
 * @param {string} text
 * @param {number} limit
 * @returns {string}
 */
export function clipTemuProductNameToLimit(text, limit) {
  const max = Number(limit);
  const s = String(text ?? '');
  if (!(max > 0)) return s;
  if (countTemuProductNameWeight(s) <= max) return s;
  let out = '';
  let weight = 0;
  for (const ch of s) {
    const w = ASCII_PRINTABLE.test(ch) ? 1 : 2;
    if (weight + w > max) break;
    out += ch;
    weight += w;
  }
  return out;
}

/**
 * @param {string} text
 * @param {string|number|null|undefined} cat1Id
 * @returns {{ ok: true, weight: number, limit: number } | { ok: false, weight: number, limit: number, message: string }}
 */
export function validateTemuProductNameLength(text, cat1Id) {
  const limit = resolveTemuProductNameLimit(cat1Id);
  const weight = countTemuProductNameWeight(text);
  if (weight > limit) {
    return {
      ok: false,
      weight,
      limit,
      message: `商品名称超过上限（加权 ${weight}/${limit}；字母数字英文标点计 1，其他字符计 2）`,
    };
  }
  return { ok: true, weight, limit };
}
